import { FC, useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import mavAeroIcon from '../assets/image/mav-aero_icon_32.png'
import { useGenerateInstallerSasUrl } from '../api/upload-API'

// ── Markdown document imports ──
import mdUserGuide from '../mav/UserGuide.md'
import mdGettingStarted from '../mav/GettingStarted.md'
import mdMainMenu from '../mav/MainMenu.md'
import mdMainMenubar from '../mav/MainMenubar.md'
import mdSessionManager from '../mav/SessionManager.md'
import mdSessionEditor from '../mav/SessionEditor.md'
import mdSessionSharing from '../mav/SessionSharing.md'
import mdVisualizer from '../mav/Visualizer.md'
import mdPreferences from '../mav/Preferences.md'
import mdHotkeys from '../mav/Hotkeys.md'
import mdSupport from '../mav/Support.md'
import mdVizAnalysisSets from '../mav/VisualizerAnalysisSets.md'
import mdVizLayouts from '../mav/VisualizerLayouts.md'
import mdVizViewPanes from '../mav/VisualizerViewPanes.md'
import mdVizViewNav from '../mav/VisualizerViewNavigation.md'
import mdVizViewToolbar from '../mav/VisualizerViewToolbar.md'
import mdVizViewLinkGroups from '../mav/VisualizerViewLinkGroups.md'
import mdVizViewControls from '../mav/VisualizerViewControls.md'
import mdVizViewVariables from '../mav/VisualizerViewVariables.md'
import mdVizViewSlices from '../mav/VisualizerViewSlices.md'
import mdVizSurfacePartsList from '../mav/VisualizerSurfacePartsList.md'
import mdVizSurfacePlotGraph from '../mav/VisualizerSurfacePlotGraph.md'
import mdVizFloorplots from '../mav/VisualizerFloorplots.md'
import mdVizDisplayPrefs from '../mav/VisualizerDisplayPreferences.md'
import mdVizEnvPrefs from '../mav/VisualizerEnvironmentPreferences.md'
import mdVizMediaCapture from '../mav/VisualizerMediaCapture.md'
import mdVizSlices from '../mav/VisualizerSlicesPanel.md'
import mdVizColorMaps from '../mav/VisualizerColorMapsPanel.md'
import mdVizLIC from '../mav/VisualizerLICPanel.md'
import mdFAQ from '../mav/FAQ.md'
import mdSliceResults from '../mav/SliceResults.md'

// ── Searchable docs list ──
const ALL_DOCS = [
  { id: 'user-guide', title: 'User Guide', mdUrl: mdUserGuide },
  { id: 'getting-started', title: 'Getting Started', mdUrl: mdGettingStarted },
  { id: 'main-menu', title: 'Main Menu', mdUrl: mdMainMenu },
  { id: 'main-menubar', title: 'Main Menubar', mdUrl: mdMainMenubar },
  { id: 'session-manager', title: 'Session Manager', mdUrl: mdSessionManager },
  { id: 'session-editor', title: 'Session Editor', mdUrl: mdSessionEditor },
  { id: 'session-sharing', title: 'Session Sharing', mdUrl: mdSessionSharing },
  { id: 'visualizer', title: 'Visualizer', mdUrl: mdVisualizer },
  { id: 'viz-analysis-sets', title: 'Analysis Sets', mdUrl: mdVizAnalysisSets },
  { id: 'viz-layouts', title: 'Layouts', mdUrl: mdVizLayouts },
  { id: 'viz-view-panes', title: 'View Panes', mdUrl: mdVizViewPanes },
  { id: 'viz-view-nav', title: 'View Navigation', mdUrl: mdVizViewNav },
  { id: 'viz-view-toolbar', title: 'View Toolbar', mdUrl: mdVizViewToolbar },
  { id: 'viz-view-link-groups', title: 'View Link Groups', mdUrl: mdVizViewLinkGroups },
  { id: 'viz-view-controls', title: 'View Controls', mdUrl: mdVizViewControls },
  { id: 'viz-view-variables', title: 'View Variables', mdUrl: mdVizViewVariables },
  { id: 'viz-view-slices', title: 'View Slices', mdUrl: mdVizViewSlices },
  { id: 'viz-surface-parts', title: 'Surface Parts List', mdUrl: mdVizSurfacePartsList },
  { id: 'viz-surface-plot', title: 'Surface Plot Graph', mdUrl: mdVizSurfacePlotGraph },
  { id: 'viz-floorplots', title: 'Floorplots', mdUrl: mdVizFloorplots },
  { id: 'viz-display-prefs', title: 'Display Options', mdUrl: mdVizDisplayPrefs },
  { id: 'viz-env-prefs', title: 'Environment Options', mdUrl: mdVizEnvPrefs },
  { id: 'viz-media-capture', title: 'Media Capture', mdUrl: mdVizMediaCapture },
  { id: 'viz-slices', title: 'Slices Panel', mdUrl: mdVizSlices },
  { id: 'viz-color-maps', title: 'Color Maps Panel', mdUrl: mdVizColorMaps },
  { id: 'viz-lic', title: 'LIC Panel', mdUrl: mdVizLIC },
  { id: 'slice-results', title: 'Slice Results', mdUrl: mdSliceResults },
  { id: 'preferences', title: 'Preferences', mdUrl: mdPreferences },
  { id: 'hotkeys', title: 'Hotkeys', mdUrl: mdHotkeys },
  { id: 'support', title: 'Support', mdUrl: mdSupport },
  { id: 'faq', title: 'FAQ', mdUrl: mdFAQ }
]

interface MavNavBarProps {
  /** Called when a search result is selected while already on the docs page */
  onSearchSelect?: (docId: string) => void
  /** Optional scroll-to handler for section links like Setup / Tutorials (on landing page) */
  scrollTo?: (id: string) => (e: React.MouseEvent) => void
}

const MavNavBar: FC<MavNavBarProps> = ({ onSearchSelect, scrollTo }) => {
  const navigate = useNavigate()
  const [, getSasUrl] = useGenerateInstallerSasUrl()
  const [isDownloading, setIsDownloading] = useState(false)

  /* ── Search state ── */
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<
    { docId: string; title: string; snippet: string }[]
  >([])
  const [searchOpen, setSearchOpen] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cache = useRef<Record<string, string>>({})

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setSearchOpen(false)
      return
    }
    const results: { docId: string; title: string; snippet: string }[] = []
    const lowerQuery = query.toLowerCase()

    for (const doc of ALL_DOCS) {
      if (!cache.current[doc.id]) {
        try {
          const res = await fetch(doc.mdUrl)
          cache.current[doc.id] = await res.text()
        } catch {
          continue
        }
      }
      const text = cache.current[doc.id]
      const lowerText = text.toLowerCase()
      const idx = lowerText.indexOf(lowerQuery)
      if (idx !== -1 || doc.title.toLowerCase().includes(lowerQuery)) {
        let snippet = ''
        if (idx !== -1) {
          const start = Math.max(0, idx - 40)
          const end = Math.min(text.length, idx + query.length + 80)
          snippet =
            (start > 0 ? '…' : '') +
            text
              .slice(start, end)
              .replace(/[#*`~_|]/g, '')
              .replace(/\n+/g, ' ') +
            (end < text.length ? '…' : '')
        } else {
          snippet =
            text
              .slice(0, 120)
              .replace(/[#*`~_|]/g, '')
              .replace(/\n+/g, ' ') + '…'
        }
        results.push({ docId: doc.id, title: doc.title, snippet })
      }
    }
    setSearchResults(results)
    setSearchOpen(true)
  }, [])

  const onSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const q = e.target.value
      setSearchQuery(q)
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      searchTimerRef.current = setTimeout(() => performSearch(q), 250)
    },
    [performSearch]
  )

  const handleResultClick = useCallback(
    (docId: string) => {
      setSearchQuery('')
      setSearchResults([])
      setSearchOpen(false)
      if (onSearchSelect) {
        onSearchSelect(docId)
      } else {
        navigate(`/mav-aero-docs?doc=${docId}`)
      }
    },
    [onSearchSelect, navigate]
  )

  const handleDownload = async () => {
    try {
      setIsDownloading(true)
      const result = await getSasUrl({
        data: { uploadType: 'MAV_INSTALLER_FILE', blobName: 'MAV-Installer.exe', raceTeamId: '' }
      })
      if (result?.data?.sasUrl) {
        const link = document.createElement('a')
        link.href = result.data.sasUrl
        link.download = 'MAV-Installer.exe'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        alert('Download failed. Please contact support.')
      }
    } catch {
      alert('Download failed. Please contact support.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <nav className="mav-nav">
      <Link className="mav-nav-logo" to="/mav-aero">
        <img src={mavAeroIcon} alt="MAV-Aero" width="28" height="28" />
        MAV-Aero
      </Link>

      <ul className="mav-nav-links">
        {scrollTo ? (
          <>
            <li>
              <a href="#mav-setup" onClick={scrollTo('mav-setup')}>
                Setup
              </a>
            </li>
            <li>
              <a href="#mav-videos" onClick={scrollTo('mav-videos')}>
                Tutorials
              </a>
            </li>
            <li>
              <a href="#mav-resources" onClick={scrollTo('mav-resources')}>
                Resources
              </a>
            </li>
            <li>
              <Link to="/mav-aero-web-launcher">Launcher</Link>
            </li>
          </>
        ) : (
          <>
            <li>
              <Link to="/mav-aero#mav-setup">Setup</Link>
            </li>
            <li>
              <Link to="/mav-aero#mav-videos">Tutorials</Link>
            </li>
            <li>
              <Link to="/mav-aero#mav-resources">Resources</Link>
            </li>
            <li>
              <Link to="/mav-aero-web-launcher">Launcher</Link>
            </li>
          </>
        )}
        <li>
          <Link to="/mav-aero-docs">Docs</Link>
        </li>
      </ul>

      <div className="mav-nav-right">
        <div className="mav-search-wrap" ref={searchRef}>
          <svg
            className="mav-search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="mav-search-input"
            type="text"
            placeholder="Search documentation…"
            value={searchQuery}
            onChange={onSearchChange}
            onFocus={() => {
              if (searchResults.length > 0 || searchQuery.trim()) setSearchOpen(true)
            }}
          />
          {searchOpen && searchQuery.trim() && (
            <div className="mav-search-dropdown">
              {searchResults.length === 0 ? (
                <div className="mav-search-empty">No results for &ldquo;{searchQuery}&rdquo;</div>
              ) : (
                <>
                  <div className="mav-search-dropdown-header">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                  </div>
                  {searchResults.slice(0, 5).map(r => (
                    <div
                      key={r.docId}
                      className="mav-search-result"
                      onClick={() => handleResultClick(r.docId)}>
                      <div className="mav-search-result-title">{r.title}</div>
                      <div className="mav-search-result-snippet">{r.snippet}</div>
                    </div>
                  ))}
                  <div
                    className="mav-search-dropdown-footer"
                    onClick={() => {
                      setSearchOpen(false)
                      navigate(`/mav-aero-docs?doc=search&q=${encodeURIComponent(searchQuery)}`)
                    }}>
                    View all {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} →
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="mav-nav-cta"
          style={{ border: 'none', cursor: isDownloading ? 'wait' : 'pointer' }}>
          {isDownloading ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </nav>
  )
}

/** CSS to be included in any page using MavNavBar */
export const MAV_NAV_CSS = `
  /* ── NAV ── */
  .mav-nav {
    position: sticky;
    top: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    padding: 0 48px;
    height: 64px;
    background: #fff;
    border-bottom: 1px solid var(--mav-border);
    box-shadow: 0 1px 6px rgba(0,0,0,.06);
    gap: 24px;
  }
  .mav-nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--mav-blue);
    text-decoration: none;
    flex-shrink: 0;
  }
  .mav-nav-links {
    display: flex;
    align-items: center;
    gap: 24px;
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
  .mav-nav-links a {
    text-decoration: none;
    color: var(--mav-muted);
    font-size: .9rem;
    font-weight: 500;
    transition: color .2s;
  }
  .mav-nav-links a:hover { color: var(--mav-blue); }
  .mav-nav-cta {
    background: var(--mav-blue);
    color: #fff !important;
    padding: 8px 20px;
    border-radius: 6px;
    font-size: .9rem;
    font-weight: 500;
    cursor: pointer;
    border: none;
    flex-shrink: 0;
    transition: background .2s !important;
  }
  .mav-nav-cta:hover { background: var(--mav-blue-dark) !important; color: #fff !important; }
  .mav-nav-right {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
    flex-shrink: 0;
  }
  .mav-search-wrap {
    position: relative;
    width: 280px;
  }
  .mav-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--mav-muted);
    pointer-events: none;
  }
  .mav-search-input {
    width: 100%;
    padding: 8px 14px 8px 38px;
    border: 1px solid var(--mav-border);
    border-radius: 8px;
    font-size: .875rem;
    color: var(--mav-text);
    background: #f8fafc;
    outline: none;
    transition: border-color .2s, box-shadow .2s;
  }
  .mav-search-input:focus {
    border-color: var(--mav-blue);
    box-shadow: 0 0 0 3px rgba(0,114,206,.12);
    background: #fff;
  }
  .mav-search-input::placeholder { color: #9aa3b0; }
  .mav-search-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    left: auto;
    width: 520px;
    background: #fff;
    border: 1px solid var(--mav-border);
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(0,0,0,.12);
    max-height: 480px;
    overflow-y: auto;
    z-index: 200;
  }
  .mav-search-dropdown-header {
    padding: 10px 16px;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--mav-muted);
    border-bottom: 1px solid var(--mav-border);
  }
  .mav-search-dropdown-footer {
    padding: 12px 16px;
    font-size: .85rem;
    font-weight: 600;
    color: var(--mav-blue);
    cursor: pointer;
    border-top: 1px solid var(--mav-border);
    text-align: center;
    transition: background .12s;
  }
  .mav-search-dropdown-footer:hover { background: var(--mav-blue-light); }
  .mav-search-result {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid var(--mav-border);
    transition: background .12s;
  }
  .mav-search-result:last-child { border-bottom: none; }
  .mav-search-result:hover { background: var(--mav-blue-light); }
  .mav-search-result-title {
    font-size: .875rem;
    font-weight: 600;
    color: var(--mav-blue);
    margin-bottom: 2px;
  }
  .mav-search-result-snippet {
    font-size: .8rem;
    color: var(--mav-muted);
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .mav-search-empty {
    padding: 20px 16px;
    text-align: center;
    font-size: .85rem;
    color: var(--mav-muted);
  }

  /* ── NAV DARK MODE ── */
  body.dark .mav-nav { background: #06080c; }
  body.dark .mav-search-input { background: #1e293b; border-color: var(--mav-border); color: var(--mav-text); }
  body.dark .mav-search-input:focus { background: #0f172a; }
  body.dark .mav-search-input::placeholder { color: #64748b; }
  body.dark .mav-search-dropdown { background: #1e293b; border-color: var(--mav-border); }
  body.dark .mav-search-dropdown-footer:hover { background: rgba(0,114,206,.15); }
  body.dark .mav-search-result:hover { background: rgba(0,114,206,.15); }

  @media (max-width: 768px) {
    .mav-nav { padding: 0 20px; gap: 12px; }
    .mav-nav-links { position: static; transform: none; gap: 16px; }
    .mav-search-wrap { width: 180px; }
    .mav-search-dropdown { width: 320px; right: 0; left: auto; }
  }
`

export default MavNavBar
